/* eslint-disable @typescript-eslint/no-empty-function */

/**
 * Compatibility adapter for hosts that still expect the legacy imperative viewer API.
 *
 * This class bridges older integrations, script/UMD usage, and dev-shell flows into the
 * React-based BV Express runtime exposed by `ViewerExpressLegacyWrapper`.
 *
 * Current responsibilities:
 * - Lazily mount the React viewer into the provided `domElement`.
 * - Route by loading screen config from a package path, or accept a prebuilt screen config.
 * - Forward legacy host callbacks such as route change, route complete, page change,
 *   annotation requests, glossary requests, and auth token lookup.
 * - Expose compatibility helpers for theme updates, annotations, spotlight, search, and media.
 * - Clean up the mounted root, generated container, observers, and injected theme styles on unmount.
 *
 * Prefer `ViewerExpressReactComponent` for React-first consumers. Use this adapter when the host
 * still needs the older `viewerApi`-style surface.
 */

import { createRef } from "react";
import { createRoot, Root } from "react-dom/client";
import {
  ViewerConfig,
  RendererTypes,
  CoursewareStructure,
  IAnnotation,
  Route,
  Media,
} from "@bronte/viewer/src/main";
import type { Link } from "@bronte/viewer/src/data/ViewerConfig";
import {
  AnnotationsAPI,
  GetAuthTokenCallback,
  SpotlightAPI,
  SearchAPI,
  MediaAPI,
  SpotlightApplyOptions,
  ThemeOptions,
  ThemeVariants,
} from "../types";
import { getEncryptedFilesList } from "@bronte/offline-services/src/utils/DataUtils";
import { debounce } from "lodash";
import * as devShellOnlyUtils from "./utils/devShellOnlyUtils";
import * as fileUtils from "./utils/fileUtils";
import { generateCSS, generatePopoverReaderCSS, logPayload } from "./utils/themeUtils";

import {
  addAnnotation,
  editAnnotation,
  deleteAnnotation,
} from "../callbacks/annotations/annotations";
import {
  ViewerExpressLegacyWrapper,
  type ViewerExpressLegacyWrapperHandlers,
} from "./ViewerExpressLegacyWrapper";
import { DEVICE_TYPE } from "../constants";
import { DEFAULT_THEME_VARIANT, VIEWER_EXPRESS_LEGACY_ROOT_CLASS } from "../viewer/constants";
import { isValidSpotlightConfig } from "../util/spotlight";
import { JsIntegrations } from "../JsIntegrations";
import type { ViewerExpressPageData } from "../viewer/components/ViewerExpressReactComponent";

export class ViewerExpressLegacyAdapter {
  #reactRoot: Root | null = null;
  #container: HTMLElement | null = null;
  #wrapperRef = createRef<ViewerExpressLegacyWrapperHandlers>();

  #viewerConfig: ViewerConfig;
  #isLogEnabled = false;
  #prevSpotlightString = "";
  /** Mirrors full viewer `clientData.hideAnnotations`; drives `.hide-annotations` on the mounted container. */
  #hideAnnotations = false;
  /**
   * When hosts call `updateTheme` before the first `route()`, the React wrapper is not mounted yet
   * and `setThemeVariant` cannot run. Stash here and pass as `initialThemeVariant` on first mount.
   */
  #pendingThemeVariant: any = null;

  constructor(viewerConfig: ViewerConfig) {
    this.#viewerConfig = viewerConfig;

    if (!this.#validate()) {
      console.error("Invalid initialization of Bronte Express Viewer.");
      return;
    }

    this.#init();
  }

  #validate = (): boolean => {
    if (this.#viewerConfig.renderer === RendererTypes.MOBILE && !this.#viewerConfig.packagePath) {
      return false;
    }

    return true;
  };

  #init = async (): Promise<void> => {
    console.log("ViewerExpressLegacyAdapter: onReady");
  };

  #getPackagePath = (): string => {
    const { packagePath } = this.#viewerConfig;
    if (!packagePath) {
      throw new Error("Package path is required");
    }
    return packagePath;
  };

  #emitRouteChange = (elementId: string): void => {
    this.#viewerConfig.events?.onRouteChange?.({ id: elementId } as Route);
  };

  #emitRouteComplete = (elementId: string): void => {
    this.#viewerConfig.events?.onRouteComplete?.({ id: elementId } as Route);

    const { packagePath, jsIntegrations } = this.#viewerConfig;
    if (!packagePath || !jsIntegrations) {
      return;
    }

    // Match plugins-lib offline timing (initCallback setTimeout 1000ms).
    setTimeout(() => {
      void JsIntegrations.fetchDynamicJSScripts(elementId, jsIntegrations);
    }, 1000);
  };

  #routeWithConfig = (
    screenConfig: import("../types").ScreenConfig,
    elementId?: string,
    dataId?: string,
  ): void => {
    // First route can happen before wrapper ref is ready. Seed initialPage at mount-time
    // so refresh loads a screen immediately without requiring a second user action.
    if (!this.#reactRoot) {
      this.#ensureReactRoot({ screenConfig, elementId, dataId });
      return;
    }
    this.#wrapperRef.current?.route(screenConfig, elementId, dataId);
  };

  #mountReactRoot = (props: {
    onClickReferenceLink: (dataId: string, referenceId: string) => void;
    onRequestAnnotations?: (annotation: IAnnotation) => void;
    onAnnotationIconClick?: (data: any) => void;
    onRequestGlossaryData: () => Promise<any[]>;
    onRequestAuthToken?: GetAuthTokenCallback;
    onMediaClick?: (data: Media) => void;
    onMediaPlay?: (data: Media) => void;
    onLinkClick?: (data: Link) => void;
    deviceType?: ViewerConfig["deviceType"];
    isOnline?: ViewerConfig["isOnline"];
    renderer?: ViewerConfig["renderer"];
    onScreenChange?: (elementId: string) => void;
    onScreenRendered?: (elementId: string) => void;
    onPageChange?: (data: { pageNumber: string }) => void;
    onRequestScreenPageNumbers?: (
      elementId: string,
    ) => Array<{ pageNumber: string; dataId?: string }>;
    initialPage?: ViewerExpressPageData;
  }): void => {
    const { domElement: rootElement, env } = this.#viewerConfig;

    if (!rootElement.style.height && !rootElement.style.maxHeight) {
      rootElement.style.height = "100vh";
      rootElement.style.position = "relative";
      rootElement.style.overflow = "hidden";
    }

    const container = document.createElement("div");
    // container.id = VIEWER_EXPRESS_CONTAINER_ID;
    container.className = VIEWER_EXPRESS_LEGACY_ROOT_CLASS;

    rootElement.appendChild(container);

    this.#container = container;
    this.#syncHideAnnotationsClass();
    const themeForMount = this.#pendingThemeVariant;
    this.#pendingThemeVariant = null;
    this.#reactRoot = createRoot(container);
    this.#reactRoot.render(
      <ViewerExpressLegacyWrapper
        ref={this.#wrapperRef}
        {...props}
        env={(env as string | undefined) ?? "dev"}
        initialThemeVariant={themeForMount ?? DEFAULT_THEME_VARIANT}
      />,
    );

    console.log("ViewerExpressLegacyAdapter: React root mounted 103");
  };

  /** Applies `hide-annotations` from viewer-express `index.scss` (same behavior as plugins-lib on document root). */
  #syncHideAnnotationsClass = (): void => {
    if (!this.#container) return;
    this.#container.classList.toggle("hide-annotations", this.#hideAnnotations);
  };

  getCoursewareStructure = async (): Promise<CoursewareStructure> => {
    return await devShellOnlyUtils.getCoursewareStructure(this.#getPackagePath());
  };

  getThemeVariants = async (): Promise<ThemeVariants> => {
    return await devShellOnlyUtils.getThemeVariants(this.#getPackagePath());
  };

  #referenceLinkCallback = async (dataId: string, referenceId: string) => {
    console.log(
      "At wrapper referenceLinkCallback called with dataId:",
      dataId,
      "and referenceId:",
      referenceId,
    );

    const packagePath = this.#getPackagePath();
    if (!referenceId) {
      throw new Error("Reference ID is required");
    }
    const annotationData = await fileUtils.getAnnotation(referenceId, packagePath);

    const screenId = annotationData.tid;

    if (screenId) {
      const screenConfig = await fileUtils.getScreenConfig(screenId, packagePath);

      this.#wrapperRef.current?.route(screenConfig, screenId, annotationData.rid);
    } else {
      console.error("ReferenceId not found in the annotation data");
    }
  };

  #getGlossaryData = async (): Promise<any[]> => {
    const glossaryData = await fileUtils.getGlossary(this.#getPackagePath());
    return glossaryData ?? [];
  };

  getEncryptedFilesList = (elementId: string): string[] => getEncryptedFilesList(elementId);

  #ensureReactRoot = (initialPage?: ViewerExpressPageData): void => {
    if (this.#reactRoot) return;

    const callbackFunctions = {
      onClickReferenceLink:
        this.#viewerConfig.events?.onClickReferenceLink ?? this.#referenceLinkCallback,
      onRequestAnnotations:
        (
          this.#viewerConfig.events as
            | { onAnnotationClicked?: (a: IAnnotation) => void }
            | undefined
        )?.onAnnotationClicked ?? this.#viewerConfig.events?.onAnnotationRequest,
      onAnnotationIconClick: this.#viewerConfig.events?.onAnnotationIconClick,
      onScreenChange: this.#emitRouteChange,
      onScreenRendered: this.#emitRouteComplete,
      onRequestGlossaryData:
        this.#viewerConfig.events?.onRequestGlossaryData ?? this.#getGlossaryData,
      onRequestAuthToken: this.#viewerConfig.getAuthToken,
      onPageChange: this.#viewerConfig.events?.onPageChange,
      onPageScroll: this.#viewerConfig.events?.onPageScroll,
      onRequestScreenPageNumbers: this.#viewerConfig.events?.onRequestScreenPageNumbers,
      onMediaClick: this.#viewerConfig.events?.onMediaClick,
      onMediaPlay: this.#viewerConfig.events?.onMediaPlay,
      onLinkClick: this.#viewerConfig.events?.onLinkClick,
      deviceType: this.#viewerConfig.deviceType,
      isOnline: this.#viewerConfig.isOnline,
      packagePath: this.#viewerConfig.packagePath,
      renderer: this.#viewerConfig.renderer,
      initialPage,
    };

    this.#mountReactRoot(callbackFunctions);

    // First screen is shown without calling route(), so fire onRouteChange once for the host.
    if (initialPage?.elementId) {
      this.#emitRouteChange(initialPage.elementId);
    }
  };

  #debouncedRoute = async (
    elementId: string,
    subTarget: {
      annotation?: IAnnotation;
      dataId?: string;
      text?: string;
      screenId?: string;
    } = {},
  ): Promise<void> => {
    const resolvedElementId =
      elementId ||
      subTarget?.annotation?.screenId ||
      subTarget?.annotation?.elementId ||
      subTarget?.screenId;

    console.log("ViewerExpressLegacyAdapter: #debouncedRoute called with elementId:", {
      elementId,
      subTarget,
    });
    if (!resolvedElementId) return;

    const screenConfig = await fileUtils.getScreenConfig(resolvedElementId, this.#getPackagePath());
    const targetDataId = subTarget?.dataId ?? subTarget?.annotation?.dataElementId;

    this.#routeWithConfig(screenConfig, elementId, targetDataId);
  };

  route = debounce(this.#debouncedRoute, 500, {
    leading: true,
    trailing: false,
  });

  /**
   * Route directly with a pre-built screenConfig (bypasses package file lookup).
   * Useful for standalone demos or hosts that already have the screenConfig in hand.
   */
  routeWithScreenConfig = (
    screenConfig: import("../types").ScreenConfig,
    elementId?: string,
    dataId?: string,
  ): void => {
    this.#routeWithConfig(screenConfig, elementId, dataId);
  };

  getEventService = () => ({
    publish: (_topic: string, _payload?: unknown) => {},
    subscribe: (_topic: string, _handler: (...args: unknown[]) => void) => () => {},
  });

  updateTheme = async (themeOptions: ThemeOptions): Promise<void> => {
    const deviceType = this.#viewerConfig.deviceType ?? DEVICE_TYPE.WEB;
    let updatedThemeVariant: any;
    let themeVariant: any;

    if (deviceType === DEVICE_TYPE.WEB) {
      themeVariant = await fileUtils.getThemeVariant(
        themeOptions.variantId,
        this.#getPackagePath(),
      );
    } else {
      themeVariant = themeOptions;
    }

    if (!themeVariant) {
      console.error("Specified theme variant id not found.");
      return;
    }

    const variantName = themeVariant.variantName;
    const parsedConfig = JSON.parse(themeVariant.config);
    updatedThemeVariant = {
      ...parsedConfig,
      variantName,
      css: parsedConfig.css + generateCSS(themeOptions),
      popoverCss: generatePopoverReaderCSS(themeOptions),
      continuousScroll: themeOptions.continuousScroll,
    };

    if (this.#wrapperRef.current) {
      this.#wrapperRef.current.setThemeVariant(updatedThemeVariant);
    } else {
      this.#pendingThemeVariant = updatedThemeVariant;
    }
  };

  /** Prints the current screen (same surface as full viewer `viewerApi.print`). */
  print = (): void => {
    logPayload(this.#isLogEnabled, "Print payload", false);
    if (!this.#wrapperRef.current) {
      console.error("Unable to print: viewer content is not mounted.");
      return;
    }
    this.#wrapperRef.current.print();
  };

  media = (): MediaAPI => {
    return {
      play: (): void => {},
      pause: (): void => {},
    };
  };

  annotations = (): AnnotationsAPI => {
    return {
      load: (annotations: IAnnotation[], options?: { hideAnnotations?: boolean }): void => {
        logPayload(this.#isLogEnabled, "BVE Load annotation payload", annotations);

        for (const annotation of annotations) {
          addAnnotation(annotation);
        }

        if (options?.hideAnnotations !== undefined) {
          this.#hideAnnotations = options?.hideAnnotations;
          this.#syncHideAnnotationsClass();
        }
      },
      add: (annotation: IAnnotation): void => {
        logPayload(this.#isLogEnabled, "Add annotation payload", annotation);
        addAnnotation(annotation);
      },
      edit: (annotation: IAnnotation): void => {
        logPayload(this.#isLogEnabled, "Edit annotation payload", annotation);
        editAnnotation(annotation);
      },
      delete: (annotation: IAnnotation): void => {
        logPayload(this.#isLogEnabled, "Delete annotation payload", annotation);
        deleteAnnotation(annotation);
      },
      show: (showAnnotations = true): void => {
        logPayload(this.#isLogEnabled, "Show annotation payload", showAnnotations);
        this.#hideAnnotations = !showAnnotations;
        this.#syncHideAnnotationsClass();
      },
      clearSelection: (): void => {
        logPayload(this.#isLogEnabled, "Clear selection payload", false);
      },
    };
  };

  setIsAppBackgroundStatus = (_isBackGround: boolean): void => {};

  spotlight = (): SpotlightAPI => {
    return {
      apply: (options: SpotlightApplyOptions): void => {
        logPayload(this.#isLogEnabled, "Spotlight apply payload", options);

        if (!this.#wrapperRef.current) {
          return;
        }

        const items = Array.isArray(options) ? options : [options];
        const validItems = items.filter((item) => isValidSpotlightConfig(item));
        if (!validItems.length) {
          logPayload(
            this.#isLogEnabled,
            "Exiting early! Either 'selector' or 'mark' are missing",
            options,
          );
          return;
        }

        const currentSpotlightString = JSON.stringify(options);
        if (this.#prevSpotlightString === currentSpotlightString) {
          logPayload(
            this.#isLogEnabled,
            "Exiting early! 'Spotlight.apply' is called again with the same options",
            false,
          );
          return;
        }
        this.#prevSpotlightString = currentSpotlightString;

        this.#wrapperRef.current.applySpotlight(options);
      },
      clear: (): void => {
        logPayload(this.#isLogEnabled, "Spotlight clear", false);
        this.#prevSpotlightString = "";
        this.#wrapperRef.current?.clearSpotlight();
      },
    };
  };

  restart = (parentActivityId: string | undefined): void => {
    logPayload(this.#isLogEnabled, "Restart parentActivityId payload", parentActivityId ?? "");
    if (!parentActivityId) {
      console.error("Unable to reset the course.");
      return;
    }
  };

  search = (): SearchAPI => {
    return {
      clear: (): void => {
        this.spotlight().clear();
      },
    };
  };

  onNetworkStatusChange = (isOnline: boolean): void => {
    logPayload(this.#isLogEnabled, "onNetworkStatusChange payload", isOnline);
  };

  enableLog = (): void => {
    this.#isLogEnabled = true;
  };

  disableLog = (): void => {
    this.#isLogEnabled = false;
  };

  unmount = (): void => {
    this.#wrapperRef.current?.destroyPageObserver();
    if (this.#reactRoot) {
      try {
        this.#reactRoot.unmount();
        if (this.#container) {
          this.#container.innerHTML = "";
        }
        this.#reactRoot = null;
      } catch (error) {
        console.warn("Error during unmount:", error);
      }
    }

    if (this.#container && this.#container.parentNode) {
      this.#container.parentNode.removeChild(this.#container);
      this.#container = null;
    }

    this.#pendingThemeVariant = null;

    const existingStyle = document.getElementById("viewer-express-theme-css");
    if (existingStyle) {
      existingStyle.remove();
    }

    const existingPopoverStyle = document.getElementById("viewer-express-popover-css");
    if (existingPopoverStyle) {
      existingPopoverStyle.remove();
    }
  };
}
